import React, { useState, useMemo } from 'react';
import { Calculator } from '../../../types';
import { Icon } from '../../Icon';
import { CalculatorShell } from '../CalculatorShell';
import { maskNumber, maskInteger, parseBrazilianNumber } from '../../../utils/formatUtils';

interface RescisaoCalculatorProps {
  calculator: Calculator;
  onBack: () => void;
}

const RescisaoCalculator = () => {
  const [salary, setSalary] = useState<string>('5000');
  const [monthsWorked, setMonthsWorked] = useState<string>('24');
  const [dependents, setDependents] = useState<string>('0');
  const [hasNotice, setHasNotice] = useState<boolean>(false);
  const [resignationType, setResignationType] = useState<'justa' | 'injusta'>('justa');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const sal = parseFloat(parseBrazilianNumber(salary)) || 0;
    const months = parseInt(parseBrazilianNumber(monthsWorked)) || 0;
    const deps = parseInt(parseBrazilianNumber(dependents)) || 0;

    if (sal <= 0 || months <= 0) return;

    // Salário proporcional ao mês trabalhado
    const proportionalSalary = (sal / 30) * (months % 30);

    // Férias vencidas (se não tirou)
    const vacationDaysPerYear = 30;
    const yearsWorked = Math.floor(months / 12);
    const vacationDaysAccrued = yearsWorked * vacationDaysPerYear;
    const vacationValue = (sal / 30) * vacationDaysAccrued;

    // 13º proporcional
    const thirteenthProportional = (sal / 12) * months;

    // Multa rescisória
    let fineMultiplier = 0;
    if (resignationType === 'justa') {
      if (months < 12) fineMultiplier = 1 / 3;
      else if (months < 24) fineMultiplier = 2 / 3;
      else fineMultiplier = 1;
    } else { // injusta (sem justa causa)
      fineMultiplier = 0.4; // 40% do saldo
    }

    // FGTS acumulado (8% ao mês)
    const fgtsRate = 0.08;
    const fgtsAccumulated = sal * fgtsRate * months;

    // Multa do FGTS (40% em demissão sem justa causa, 20% em pedido)
    const fgtsFine = resignationType === 'injusta' ? fgtsAccumulated * 0.4 : 0;

    // Base para cálculo da multa
    const fineBase = proportionalSalary + vacationValue + thirteenthProportional;
    const fineValue = fineBase * fineMultiplier;

    // Aviso prévio
    const noticeDays = hasNotice ? 0 : (resignationType === 'justa' ? 30 : 30);
    const noticeValue = (sal / 30) * noticeDays;

    // Total a receber
    const totalReceive = proportionalSalary + vacationValue + thirteenthProportional + fineValue + fgtsAccumulated + fgtsFine - noticeValue;

    setResultState({
      salary: sal,
      monthsWorked: months,
      proportionalSalary,
      vacationValue,
      thirteenthProportional,
      fgtsAccumulated,
      fgtsFine,
      fineValue,
      fineMultiplier: (fineMultiplier * 100).toFixed(0),
      noticeValue,
      noticeDays,
      totalReceive,
      resignationType,
      hasNotice
    });
  };

  const hasResult = resultState !== null;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-red-50 to-slate-50 rounded-full border border-red-200 mb-4">
          <span className="text-2xl">👋</span>
          <span className="text-sm font-black text-theme-text uppercase tracking-wider">Rescisão</span>
        </div>
        <p className="text-theme-muted text-sm">Cálculo completo de verbas rescisórias CLT</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <h3 className="text-lg font-bold text-theme-text mb-6">Informações para Cálculo</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Salário (R$)</label>
            <div className="relative">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-xl font-black text-theme-muted pl-4">R$</span>
              <input
                type="text"
                value={salary}
                onChange={e => setSalary(maskNumber(e.target.value))}
                placeholder="Ex: 5000"
                className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 pl-12 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
              />
            </div>
            <div className="text-xs text-theme-muted mt-2">Salário mensal base</div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Tempo Trabalhado (meses)</label>
            <input
              type="text"
              value={monthsWorked}
              onChange={e => setMonthsWorked(maskInteger(e.target.value))}
              placeholder="Ex: 24"
              className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
            />
            <div className="text-xs text-theme-muted mt-2">Total de meses na empresa</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Tipo de Rescisão</label>
            <select
              value={resignationType}
              onChange={e => setResignationType(e.target.value as 'justa' | 'injusta')}
              className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
            >
              <option value="justa">Com Justa Causa</option>
              <option value="injusta">Sem Justa Causa (Pedido de Demissão)</option>
            </select>
            <div className="text-xs text-theme-muted mt-2">Define multa rescisória</div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasNotice}
                onChange={e => setHasNotice(e.target.checked)}
                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-theme-text">
                Aviso prévio cumprido?
              </span>
            </label>
            <div className="text-xs text-theme-muted mt-1">Se não, será descontado do valor a receber</div>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Rescisão
          </button>
        </div>
      </div>

      {hasResult && (
        <div className="relative">
          <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-red-500 via-pink-500 to-purple-500 opacity-10 blur-3xl" />
          <div className="relative p-10 rounded-[2.5rem] text-center shadow-2xl border-2 bg-gradient-to-br from-red-500 to-pink-500 border-red-400 text-white">
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-4xl shadow-xl">👋</div>
            </div>

            <div className="mb-6">
              <p className="text-xs font-black uppercase tracking-widest opacity-70 mb-2">Total a Receber</p>
              <p className="text-5xl font-black mb-2">R$ {resultState.totalReceive.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <div className="w-20 h-1 bg-theme-result/30 rounded-full mx-auto" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Salário Proporcional</p>
                <p className="text-2xl font-black">R$ {resultState.proportionalSalary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">{resultState.monthsWorked % 30} dias trabalhados no mês</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Férias Vencidas</p>
                <p className="text-2xl font-black">R$ {resultState.vacationValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">não gozadas</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">13º Proporcional</p>
                <p className="text-2xl font-black">R$ {resultState.thirteenthProportional.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">{resultState.monthsWorked} meses trabalhados</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Multa Rescisória</p>
                <p className="text-2xl font-black">R$ {resultState.fineValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">{resultState.fineMultiplier}% do saldo</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">FGTS Acumulado</p>
                <p className="text-2xl font-black">R$ {resultState.fgtsAccumulated.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">8% ao mês</p>
              </div>
              {resultState.fgtsFine > 0 && (
                <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                  <p className="text-xs font-black uppercase opacity-70 mb-1">Multa FGTS</p>
                  <p className="text-2xl font-black">R$ {resultState.fgtsFine.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs opacity-70 mt-1">40% do saldo</p>
                </div>
              )}
            </div>

            {resultState.noticeValue > 0 && (
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Desconto Aviso Prévio</p>
                <p className="text-2xl font-black">-R$ {resultState.noticeValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">{resultState.noticeDays} dias não cumpridos</p>
              </div>
            )}

            <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <p className="text-xs font-black uppercase opacity-70 mb-2">📋 Resumo da Rescisão</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Tipo:</span>
                  <span className="font-bold">{resultState.resignationType === 'justa' ? 'Com Justa Causa' : 'Sem Justa Causa'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tempo trabalhado:</span>
                  <span className="font-bold">{resultState.monthsWorked} meses</span>
                </div>
                <div className="flex justify-between">
                  <span>Multa rescisória:</span>
                  <span className="font-bold">{resultState.fineMultiplier}%</span>
                </div>
                <div className="flex justify-between border-t pt-1 mt-1">
                  <span>Total líquido:</span>
                  <span className="font-bold text-lg">R$ {resultState.totalReceive.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!hasResult && (
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center text-2xl mb-3">👋</div>
          <p className="font-bold text-base">Preencha os dados para calcular a rescisão</p>
        </div>
      )}
    </div>
  );
};

export default RescisaoCalculator;
